import { useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import { applyTheme, readStoredTheme } from '../lib/theme';

/** Header control: sun = switch to light, moon = switch to dark (common pattern). */
export default function ThemeToggle({ className = '' }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : readStoredTheme(),
  );

  useEffect(() => {
    setDark(document.documentElement.classList.contains('dark'));
  }, [user?.id]);

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDark(el.classList.contains('dark'));
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const toggle = useCallback(async () => {
    const next = !document.documentElement.classList.contains('dark');
    applyTheme(next);
    setDark(next);
    if (user) {
      try {
        await api.put('/users/me/settings', { darkMode: next });
      } catch {
        /* offline / session — local theme still applied */
      }
    }
  }, [user]);

  return (
    <button
      type="button"
      onClick={toggle}
      className={`relative p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors ${className}`}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}
